import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchThemeData } from '../admin/themeSlice';
import { setSession } from '../admin/sessionSlice';
import { setBackButtonUrl, setCompanyLogoUrl } from './uiSlice';
import { sendReport, extractReportIdFromResponse } from '../functions/sendReport';
import { updateGameSettings } from '../functions/updateGameSettings';

/** Session-scoped user payload — `gameover` from DB indicates completed play (>0). */
const USER_DATA_KEY = 'userData';

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

const isTrueQueryParam = (value) =>
  typeof value === "string" && value.trim().toLowerCase() === "true";

/** Reads latest `stages` status + reportId via backend. */
async function fetchStageGameover({ sessionId, organizationId, userId }) {
  try {
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
      /\/+$/,
      ""
    );
    if (!backendBase || !sessionId || !organizationId || !userId) {
      return { gameover: 0, reportId: null, status: null };
    }
    const res = await fetch(`${backendBase}/fetchReport`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionId}&${organizationId}`,
      },
      body: JSON.stringify({ userId, sessionId, organizationId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.success || !data.report) return { gameover: 0, reportId: null, status: null };
    const g = data.report.gameover;
    const rid = data.report.reportid || data.report.reportId || data.report.id;
    const st = data.report.status ?? null;
    const gameover = g == null || g === "" ? 0 : Number.isFinite(Number(g)) ? Number(g) : 0;
    const reportId = rid != null && String(rid).trim() !== "" ? String(rid).trim() : null;
    const status = st != null && String(st).trim() !== "" ? String(st).trim() : null;
    return { gameover, reportId, status };
  } catch {
    return { gameover: 0, reportId: null, status: null };
  }
}

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const save = queryParams.get('save');
      const demo = queryParams.get('demo');
      const fromMobileApp = isTrueQueryParam(queryParams.get('fromMobileApp'));

      if (save) {
        let stored = sessionStorage.getItem(USER_DATA_KEY);
        if (!stored) {
          const legacy = localStorage.getItem(USER_DATA_KEY);
          if (legacy) {
            sessionStorage.setItem(USER_DATA_KEY, legacy);
            localStorage.removeItem(USER_DATA_KEY);
            stored = legacy;
          }
        }
        if (!stored) return rejectWithValue('No saved user data found.');
        let userData = JSON.parse(stored);
        const persistedFromMobileApp = isTrueQueryParam(String(userData?.fromMobileApp ?? ""));
        const finalFromMobileApp = fromMobileApp || persistedFromMobileApp;
        if (finalFromMobileApp !== persistedFromMobileApp) {
          userData = { ...userData, fromMobileApp: finalFromMobileApp };
          sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        }
        dispatch(setSession({ sessionId: userData.sessionId, organizationId: userData.organizationId }));
        dispatch(fetchThemeData({ themeId: null }));
        // Ensure header UI is populated on "save" login flow too.
        dispatch(setCompanyLogoUrl(userData.companyLogoUrl || process.env.REACT_APP_EM_LOGO || null));
        dispatch(setBackButtonUrl(userData.backButtonRedirect || null));
        const { gameover, reportId, status } = await fetchStageGameover({
          sessionId: userData.sessionId,
          organizationId: userData.organizationId,
          userId: userData.userId,
        });
        const statusLc = status ? String(status).trim().toLowerCase() : "";
        userData = {
          ...userData,
          gameover,
          reportId: reportId || userData.reportId,
          guessMeStatus: status,
          guessMePlayed: statusLc === "completed",
        };
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
        return userData;
      }

      let userResponseData;
      if (demo) {
        console.log("working with demo now");
        function generateRandomUser() {
          const randomString = 'demo' + Math.floor(Math.random() * 10000);
          return {
            data: {
              data: {
                id: Math.floor(Math.random() * 100000),
                email: `${randomString}@gmail.com`,
                userId: randomString,
                firstName: randomString,
                lastName: randomString,
                employeeId: `EM${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
                sessionId: "demobypass",
                organizationId: "demobypass",
                gameId: null,
                role:"demobypass",
                companyLogoUrl: process.env.REACT_APP_EM_LOGO,
                backButtonRedirect: process.env.REACT_APP_BASE_URL,
                gameover: 0,
              }
            }
          };
        }
        const response = generateRandomUser();
        userResponseData = response.data.data;
      } else {
        const otp = queryParams.get('otp');
        const role = queryParams.get('role');
        if (!otp) return rejectWithValue('OTP is required for login.');

        let apiUrl = process.env.REACT_APP_FIREBASE_LOGIN_API;
        const requestData = { otp };
        let source = 'ORG_USER';
        if (role === 'GUEST_USER') {
          apiUrl = process.env.REACT_APP_FIREBASE_GUEST_USER_API;
          requestData.role = 'GUEST_USER';
          source = 'GUEST_USER';
        }

        const clientSecret =
          process.env.REACT_APP_FIREBASE_CLIENT_SECRET ||
          process.env.REACT_APP_CLIENT_SECRET;
        const headers = {
          'client-secret': clientSecret,
          'Content-Type': 'application/json',
          'Idempotency-Key': uuid4(),
        };

        const response = await axios.post(apiUrl, requestData, { headers });
        if (response.data.message !== 'LOGIN SUCCESS') {
          return rejectWithValue('Login failed');
        }
        response.data.data["role"]=source;
        userResponseData = response.data.data;
      }

      // Store session and fetch theme
      dispatch(setSession({ sessionId: userResponseData.sessionId, organizationId: userResponseData.organizationId }));
      dispatch(fetchThemeData({ themeId: null }));

      // Build user data to persist
      const userData = {
        userId: userResponseData.userId || userResponseData.id,
        token: userResponseData.token || "",
        name: (() => {
          const fn = userResponseData.firstName != null ? String(userResponseData.firstName).trim() : "";
          const ln = userResponseData.lastName != null ? String(userResponseData.lastName).trim() : "";
          const full = `${fn} ${ln}`.trim();
          if (full) return full;
          if (userResponseData.name != null && String(userResponseData.name).trim() !== "") return String(userResponseData.name).trim();
          if (userResponseData.fullName != null && String(userResponseData.fullName).trim() !== "") return String(userResponseData.fullName).trim();
          const em = userResponseData.email || userResponseData.employeeId || "";
          const s = String(em).trim();
          if (!s) return "Guest";
          return s.includes("@") ? s.split("@")[0] : s;
        })(),
        email: userResponseData.email || userResponseData.employeeId,
        sessionId: userResponseData.sessionId,
        organizationId: userResponseData.organizationId,
        gameId: userResponseData.gameId,
        source: demo ? 'DEMO' : 'ORG_USER',
        expiry: Date.now() + 24 * 60 * 60 * 1000,
        role:userResponseData.role,
        employeeId: userResponseData.employeeId || userResponseData.employeeid || null,
        companyLogoUrl: process.env.REACT_APP_EM_LOGO,
        backButtonRedirect: process.env.REACT_APP_BASE_URL,
        gameover: 0,
        fromMobileApp,
        lobby: userResponseData.lobby || null
      };

      // Guest flow: if backend doesn't provide a report id yet,
      // use the login `id` (userId) as reportId so it gets stored in DB and returned by fetchReport.
      // This avoids blank reportid for join-by-link/pin and other guest flows.
      try {
        const roleUpper = String(userData.role ?? userData.user_type ?? userData.source ?? "").trim().toUpperCase();
        const isGuest = roleUpper === "GUEST_USER";
        const hasRid = userData.reportId != null && String(userData.reportId).trim() !== "";
        if (isGuest && !hasRid && userData.userId != null && String(userData.userId).trim() !== "") {
          userData.reportId = String(userData.userId).trim();
        }
      } catch {
        // ignore
      }

      // Join by link/QR: create the user's stage row immediately on login (first insert).
      // This ensures DB has the user row even before entering the game flow.
      if (!demo) {
        try {
          const hasLobby = userData.lobby != null && String(userData.lobby).trim() !== "";
          if (hasLobby) {
            const lobby = String(userData.lobby).trim();
            const pinMatch = lobby.match(/LOBBY-(\d{5})/i);
            const pin = pinMatch?.[1] ? String(pinMatch[1]) : null;

            // Ensure reportId exists before inserting the stage row (so reportid is not NULL).
            if (userData.reportId == null || String(userData.reportId).trim() === "") {
              const roleUpper = String(userData.role ?? userData.user_type ?? userData.source ?? "").trim().toUpperCase();
              const isGuest = roleUpper === "GUEST_USER";
              const token = String(userData.token ?? "").trim();
              if (isGuest || token) {
                const seeded = await sendReport({
                  sessionId: userData.sessionId,
                  organizationId: userData.organizationId,
                  role: userData.role ?? userData.user_type ?? userData.source ?? "ORG_USER",
                  token: isGuest ? "" : token,
                  name: userData.name,
                  email: userData.email ?? null,
                  userId: userData.userId,
                  gameId: userData.gameId ?? "",
                  points: 0,
                  time: "00:00",
                  ans: `GUESS_ME:${lobby}`,
                  user_type: userData.role ?? userData.user_type ?? null,
                  guestScoreMinimal: isGuest,
                });
                const seededRid = extractReportIdFromResponse(seeded, userData.userId);
                if (seededRid) userData.reportId = String(seededRid).trim();
              }
            }

            const stage = await updateGameSettings({
              token: `${userData.sessionId}&${userData.organizationId}`,
              userId: userData.userId,
              themeName: "guess_me",
              lobby_name: lobby,
              ...(pin ? { lobby_pin: pin } : {}),
              email: userData.email ?? null,
              fullName: userData.name ?? null,
              employeeId: userData.employeeId ?? null,
              user_type: userData.role ?? null,
              reportid: userData.reportId ?? null,
              show_question: true,
            });
            if (stage?.stageId) {
              try {
                const prev = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
                sessionStorage.setItem(
                  "gameSettings",
                  JSON.stringify({ ...prev, stageId: stage.stageId, lobby_name: lobby, ...(pin ? { lobby_pin: pin } : {}) })
                );
              } catch {
                // ignore
              }
            }
          }
        } catch {
          // ignore
        }
      }

      if (!demo) {
        const { gameover, reportId, status } = await fetchStageGameover({
          sessionId: userData.sessionId,
          organizationId: userData.organizationId,
          userId: userData.userId,
        });
        userData.gameover = gameover;
        userData.guessMeStatus = status;
        userData.guessMePlayed = String(status || "").trim().toLowerCase() === "completed";
        if (reportId) {
          userData.reportId = reportId;
        }

      }

      // Fetch organization details if not demo
      if (!demo) {
        const orgRes = await axios.get(`${process.env.REACT_APP_ORG_API}/${userResponseData.organizationId}`);
         console.log(orgRes);
        if (orgRes.data?.message === 'ORGANIZATIONS_FETCHED_SUCCESSFULLY') {
          console.log(orgRes.data);
          console.log(orgRes.data?.organization.companyLogo);
          if(!orgRes.data?.organization?.companyLogo){
            userData.companyLogoUrl = process.env.REACT_APP_EM_LOGO;
          }else{
            const s3Source = process.env.REACT_APP_S3_SOURCE || "";
            const base = s3Source.endsWith("/") ? s3Source : `${s3Source}/`;
            userData.companyLogoUrl = `${base}${orgRes.data.organization.companyLogo}`;
          }
          userData.backButtonRedirect = `${process.env.REACT_APP_BASE_URL}/game-detail/${userResponseData.gameId}`;
          console.log("setcompany url",userData.companyLogoUrl);
        }
      }
      
      dispatch(setCompanyLogoUrl(userData.companyLogoUrl));
      dispatch(setBackButtonUrl(userData.backButtonRedirect || null));
      try {
        localStorage.removeItem(USER_DATA_KEY);
      } catch (_) {
        /* ignore */
      }
      sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      return userData;
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem(USER_DATA_KEY);
        try {
          localStorage.removeItem(USER_DATA_KEY);
        } catch (_) {
          /* ignore */
        }
      }
      const data = error.response?.data;
      const apiMsg =
        (typeof data?.message === 'string' && data.message) ||
        data?.err?.message;
      return rejectWithValue(apiMsg || error.message);
    }
  }
);
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    status: 'idle',
    error: null
  },
  reducers: {
    logout: state => {
      state.user = null;
      sessionStorage.removeItem(USER_DATA_KEY);
      try {
        localStorage.removeItem(USER_DATA_KEY);
      } catch (_) {
        /* ignore */
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.user = null;
      });
  }
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;

